// v18

import { useEffect, useState, useMemo, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import ListLayout from './ListLayout'
import MainButton from './MainButton'
import useData from '../../common/hooks/useData'
import ResourceListItems from './ResourceListItems'
import { parseResourcePath } from '../../common/helpers'
import FilterModalContent from '../common/FilterModalContent'
import useModal from '../../common/hooks/useModal'
import useFilter from '../../common/hooks/useFilter'
import ResourceListSelectedItems from './ResourceListSelectedItems'
import ResourceListTableColumns from './ResourceListTableColumns'
import ResourceListPagination from './ResourceListPagination'
import ResourceListTabFilters from './ResourceListTabFilters'
import ResourceListFilters from './ResourceListFilters'
import ResourceListTitle from './ResourceListTitle'
import ResourceListAddButton from './ResourceListAddButton'
import ResourceListItemViewsToggle from './ResourceListItemViewsToggle'
import Button from '../common/Button'
import ErrorMessage from '../common/ErrorMessage'
import SectionContainer from '../common/SectionContainer'
import useScrollMeasure from '../../common/hooks/useScrollMeasure'
import PullToRefresh from '../common/PullToRefresh'

const initialCursorOpts = {
    current: {
        after: '',
        before: '',
    },
    following: {
        after: '',
        before: '',
    },
}

function ResourceList({
    // Data from parent
    items: propItems,
    itemsLoading: propItemsLoading = null,

    // Data from URL
    apiUrl,
    initialQuery = {},
    getItemsFromResponse,
    mapItems,
    filterItems,
    revalidateInterval,

    // Paths to items
    newItemPath,
    itemClickPath,
    onItemClick,

    // General UI
    title,
    subtitle,
    smallTitle = false,
    renderMainButton = false,
    newItemLabel,
    newItemBtnText,
    emptyText,
    hideCount,
    minimal,
    searchOnly,
    flushSpaces,
    searchPlaceholder,
    getIncludedHtml,
    enablePullToRefresh = false,

    // Item display
    fields,
    itemViews = ['list'],
    getGroup,

    // Item actions
    actions,

    // Filters
    filters,
    hideFilterTags = false,

    // Selectable
    isSelectable = false,
    togglesSelectable = false,
    selectedIdField = 'id',
    preselectedIds,
    selectLimit,
    onSelectedItemsChange,
    onSingleItemSelect,
    getSelectedItemText,
    apiRequestSelectedItems = true,
    errors,
    bulkSelectActions = [],
    allBulkSelectActions = [],

    // Events
    onResponseLoaded,

    // Pagination
    paginated,
    cursorPagination,
    paginationLimit,
    itemsNav,

    // Table
    isTable = false,
    tableColumns,
}) {
    /**
     * Hooks
     */

    const allParams = useParams()
    const [selectedIds, setSelectedIds] = useState(
        selectedIdField ? preselectedIds || [] : null,
    )
    const [selectedAll, setSelectedAll] = useState(false)
    const [isSelectableFinal, setIsSelectableFinal] = useState(isSelectable)

    const navigate = useNavigate()
    const { setModal } = useModal()

    const [page, setPage] = useState(paginated && !cursorPagination ? 1 : null)
    const [itemsView, setItemsView] = useState(itemViews?.[0] || 'list')
    const [totalPages, setTotalPages] = useState(
        paginated && !cursorPagination ? 1 : null,
    )
    const [totalResults, setTotalResults] = useState(null)
    const [cursorOpts, setCursorOpts] = useState(
        paginated && cursorPagination ? initialCursorOpts : null,
    )
    const ref = useRef(undefined)

    const { scrollTop } = useScrollMeasure()

    const hasAllBulkSelect = allBulkSelectActions.length > 0

    /**
     * Filtering
     */

    const {
        filterQueryUrlObj = {},
        currFilter,
        currSort,
        currTabFilterKey,
        filterSetters,
        hasActiveFilter,
        currQueryStr,
    } = useFilter(filters)

    /**
     * Data fetching
     */

    // Includes all items from infinite pagination,
    // or current items if no pagination or cursor is used
    const [allItemsArr, setAllItemsArr] = useState(propItems || [])

    // Every time a page or filter changes,
    // output a new request URL
    const [url, stripPaginateUrl] = useMemo(
        function () {
            if (propItems || !apiUrl) return [null, null]
            let baseUrlStr = `${parseResourcePath(apiUrl, allParams)}`
            const urlObj = new URL(baseUrlStr, window.location.origin)

            if (urlObj.searchParams.size > 0) {
                throw new Error('Invalid url:' + baseUrlStr)
            }

            // Initial query
            for (const [k, v] of Object.entries(initialQuery)) {
                if (v === undefined) continue
                urlObj.searchParams.set(k, v)
            }

            // Pagination
            if (paginated) {
                urlObj.searchParams.set('page', page)
                if (cursorPagination) {
                    if (cursorOpts?.following?.after) {
                        urlObj.searchParams.set(
                            'after',
                            cursorOpts?.following?.after,
                        )
                    }
                    if (cursorOpts?.following?.before) {
                        urlObj.searchParams.set(
                            'before',
                            cursorOpts?.following?.before,
                        )
                    }
                } else {
                    urlObj.searchParams.set('limit', paginationLimit || 20)
                }
            }

            // Filter query
            for (const [k, v] of Object.entries(filterQueryUrlObj)) {
                urlObj.searchParams.set(k, v)
            }

            // Strip pagination URL
            const stripUrlObj = new URL(urlObj)
            stripUrlObj.searchParams.delete('page')
            stripUrlObj.searchParams.delete('limit')
            stripUrlObj.searchParams.delete('after')
            stripUrlObj.searchParams.delete('before')

            const urlStr = `${urlObj.pathname}/${urlObj.search}`
            const stripUrlStr = `${stripUrlObj.pathname}/${stripUrlObj.search}`
            return [urlStr, stripUrlStr]
        },
        [page, JSON.stringify(filterQueryUrlObj), cursorOpts?.current],
    )

    // Main fetch hook
    const {
        items,
        itemsError,
        itemsLoading,
        itemsValidating,
        itemsHasNext,
        itemsPage,
        itemsSetPage,
        itemsMutate,
    } = useData(
        url || null,
        'items',
        extractItems,
        false,
        false,
        onSuccess,
        propItems,
        revalidateInterval || null,
        undefined,
        undefined,
        paginated && !cursorPagination,
    )

    const allItems = paginated && !cursorPagination ? items : allItemsArr

    // Reset page to 1 when filters change (if plain paginated)
    useEffect(
        function () {
            if (!paginated || cursorPagination) return
            setPage(1)
        },
        [JSON.stringify(filterQueryUrlObj)],
    )

    // Currently only sets secondary info,
    // total pages, results, and cursor data if applicable
    function onSuccess(receivedData) {
        try {
            const newItems = getItemsFromResponse
                ? getItemsFromResponse(receivedData)
                : receivedData

            if (paginated && cursorPagination) {
                setCursorOpts({
                    ...cursorOpts,
                    following: {
                        after: receivedData.nextCursor || '',
                        before: receivedData.prevCursor || '',
                    },
                })
                setAllItemsArr(newItems)
            } else if (!propItems) {
                setAllItemsArr(newItems)
            }

            // These run on the last page, and internally
            // useData is set to call onSuccess after each call
            if (paginated && !cursorPagination) {
                const lastReceivedPage = ([...receivedData] || []).sort(
                    (a, b) => b?.page - a?.page,
                )?.[0]
                const totalPagesChanged =
                    totalPages !== lastReceivedPage.totalPages
                if (totalPagesChanged) {
                    setTotalPages(lastReceivedPage.totalPages || 0)
                }
                setTotalResults(lastReceivedPage.totalResults || 0)
                onResponseLoaded && onResponseLoaded(lastReceivedPage)
            }

            onResponseLoaded && onResponseLoaded(receivedData)
        } catch (error) {
            console.error(error)
        }
    }

    // Used in useData to extract items from response,
    // we use this insted of getItemsFromResponse only
    // because we pass the data to the function
    function extractItems(data) {
        if (!data) return []
        let result = data
        getItemsFromResponse && (result = getItemsFromResponse(data) || [])
        mapItems && (result = result.map((item) => mapItems(item, data)))
        filterItems &&
            (result = result.filter((item) => filterItems(item, data)))

        return result
    }

    function handleLoadMore() {
        itemsSetPage(itemsPage + 1)
    }

    /**
     * Selection
     */

    let selectedItemsUrl = null
    if (stripPaginateUrl && selectedIds?.length && apiRequestSelectedItems) {
        selectedItemsUrl = ''
        selectedItemsUrl += stripPaginateUrl.includes('?')
            ? `${stripPaginateUrl}&`
            : `${stripPaginateUrl}?`
        selectedItemsUrl += `${selectedIdField}s[]=${selectedIds.join(`&${selectedIdField}s[]=`)}&page=1&limit=50`
    }

    const {
        selectedItemsData,
        selectedItemsDataError,
        selectedItemsDataLoading,
    } = useData(
        selectedItemsUrl,
        'selectedItemsData',
        (data) => data?.results || [],
    )

    const selectedPropItems = propItems?.filter((item) =>
        selectedIds.includes(item[selectedIdField]),
    )

    const selectedItemsFinal =
        (apiRequestSelectedItems
            ? selectedItemsData
            : allItems.filter((item) =>
                  selectedIds.includes(item[selectedIdField]),
              )) || selectedPropItems

    useEffect(
        function () {
            if (!preselectedIds?.length) return
            setSelectedIds(preselectedIds)
        },
        [(preselectedIds || []).join(',')],
    )

    function onItemSelect(item) {
        if (hasAllBulkSelect) {
            if (selectedAll) {
                const newSelectedIds = allItems
                    .filter((i) => i[selectedIdField] !== item[selectedIdField])
                    .map((i) => i[selectedIdField])
                setSelectedIds(newSelectedIds)
                onSelectedItemsChange && onSelectedItemsChange(newSelectedIds)
            }

            setSelectedAll(false)
        }

        if (selectLimit === 1 && selectedIdField) {
            const newSelectedIds = selectedIds.includes(item[selectedIdField])
                ? []
                : [item[selectedIdField]]
            setSelectedIds(newSelectedIds)
            onSelectedItemsChange && onSelectedItemsChange(newSelectedIds)
            onSingleItemSelect && onSingleItemSelect(item)
            return
        }

        const newSelectedIds = selectedIds.includes(item[selectedIdField])
            ? selectedIds.filter((id) => id !== item[selectedIdField])
            : [...selectedIds, item[selectedIdField]]

        setSelectedIds(newSelectedIds)
        onSelectedItemsChange && onSelectedItemsChange(newSelectedIds)
    }

    /**
     * Filter actions
     */

    function onToggleSort() {
        if (paginated && cursorPagination) {
            setCursorOpts(initialCursorOpts)
        }
        filterSetters.setIsAsc(!currSort.isAsc)
    }

    function onSearchQuery(text) {
        filterSetters.setQueryStr(text)
    }

    function onFilterSubmit(data) {
        const { sortKey, filterValues, clearPrev } = data
        if (typeof sortKey !== 'undefined' && sortKey !== currSort.key) {
            filterSetters.setIsAsc(true)
            filterSetters.setSortKey(sortKey)
        }
        const newFilterValues = {}
        for (const key of Object.keys(filterValues)) {
            if (filterValues[key] === currFilter[key] && !clearPrev) continue
            newFilterValues[key] = filterValues[key]
        }
        filterSetters.setFilter(newFilterValues, clearPrev)
        if (paginated && cursorPagination) {
            setCursorOpts(initialCursorOpts)
        }
        setModal(null)
    }

    function onSortSubmit(data) {
        const { sortKey } = data
        if (typeof sortKey !== 'undefined' && sortKey !== currSort.key) {
            filterSetters.setIsAsc(true)
            filterSetters.setSortKey(sortKey)
        }
    }

    function onFilterDismiss(key) {
        const filter = filters?.filters?.[key]

        if (filter.type) {
            filterSetters.setFilter({ [key]: undefined })
            if (paginated && cursorPagination) {
                setCursorOpts(initialCursorOpts)
            }
            return
        }

        if (!filter) return
        const option = filter.options.find((o) =>
            ['undefined', undefined, 'null', null].includes(o.value),
        )
        if (!option) return
        filterSetters.setFilter({ [key]: option.value })
        if (paginated && cursorPagination) {
            setCursorOpts(initialCursorOpts)
        }
    }

    function onFiltersModalOpen() {
        if (!filters) return
        setModal(
            <FilterModalContent
                onSubmit={onFilterSubmit}
                filters={filters}
                currFilter={currFilter}
                currSort={currSort}
            />,
            'Filters',
            'modal-full',
        )
    }

    // Applicable to cursor pagination only
    function onPageNav(delta) {
        if (delta === -1) {
            setCursorOpts({
                ...cursorOpts,
                current: {
                    after: '',
                    before: cursorOpts.following.before,
                },
            })
        }
        if (delta === 1) {
            setCursorOpts({
                ...cursorOpts,
                current: {
                    after: cursorOpts.following.after,
                    before: '',
                },
            })
        }
    }

    /**
     * Render
     */

    const loading =
        (itemsLoading || propItemsLoading) &&
        !(searchOnly && url.includes('@@@@@@@@@@@'))

    const newItemUrl = newItemPath && parseResourcePath(newItemPath, allParams)

    const hasSearch = filters?.search

    function onDeselectItem(item) {
        const newSelectedIds = selectedIds.filter(
            (id) => id !== item[selectedIdField],
        )
        setSelectedIds(newSelectedIds)
        onSelectedItemsChange && onSelectedItemsChange(newSelectedIds)
        onSingleItemSelect && onSingleItemSelect(null)
    }

    let className = 'resource-list'
    if (filters?.length && items.length) className += ' has-filters'
    if (hasSearch) className += ' has-search'
    if (isTable) className += ' has-table'
    if (minimal) className += ' minimal'
    if (searchOnly) className += ' search-only'
    if (flushSpaces) className += ' flush-spaces'
    if (allItems?.length) className += ' has-results'
    if (itemsValidating && !loading) className += ' is-validating'
    if (errors?.length) className += ' has-field-errors'

    return (
        <>
            {/* For infinite lists, mutate refetches all loaded pages */}
            {Boolean(url) && enablePullToRefresh && (
                <PullToRefresh onRefresh={() => itemsMutate()} />
            )}

            <div ref={ref} className={className}>
                <header
                    className="list-header"
                    style={{
                        '--scroll-top': `${scrollTop}px`,
                    }}
                >
                    {Boolean(title) && (
                        <ResourceListTitle
                            title={title}
                            subtitle={subtitle}
                            smallTitle={smallTitle}
                        />
                    )}

                    {Boolean(newItemUrl && !renderMainButton) && (
                        <ResourceListAddButton
                            newItemBtnUrl={newItemUrl}
                            newItemBtnText={newItemBtnText}
                            loading={loading}
                        />
                    )}

                    {Boolean(currTabFilterKey || filters?.filterCombos) && (
                        <ResourceListTabFilters
                            filters={filters}
                            filterCombos={filters?.filterCombos}
                            currFilter={currFilter}
                            onSubmit={onFilterSubmit}
                            tabFilterKey={currTabFilterKey}
                        />
                    )}

                    {Boolean(
                        filters?.search ||
                            filters?.sort ||
                            Object.values(filters?.filters || [])?.some(
                                (f) => !f.tabs,
                            ),
                    ) && (
                        <ResourceListFilters
                            searchPlaceholder={searchPlaceholder}
                            hideFilterTags={hideFilterTags}
                            filters={filters}
                            currSort={currSort}
                            setSearchQuery={onSearchQuery}
                            toggleSort={onToggleSort}
                            openFilters={onFiltersModalOpen}
                            hasActiveFilter={hasActiveFilter}
                            currFilter={currFilter}
                            onFilterDismiss={onFilterDismiss}
                            onSortSubmit={onSortSubmit}
                            urlQuery={
                                stripPaginateUrl
                                    ? stripPaginateUrl.split('?')[1]
                                    : undefined
                            }
                            isSelectableFinal={isSelectableFinal}
                            selectedItems={selectedItemsFinal}
                            bulkSelectActions={[
                                ...(bulkSelectActions &&
                                selectedItemsFinal?.length
                                    ? bulkSelectActions
                                    : []),
                                ...(selectedAll
                                    ? allBulkSelectActions.map((a) => ({
                                          ...a,
                                          isAllBulk: true,
                                      }))
                                    : []),
                            ]}
                            onToggleSelect={
                                togglesSelectable
                                    ? () =>
                                          setIsSelectableFinal(
                                              !isSelectableFinal,
                                          )
                                    : null
                            }
                        />
                    )}

                    {(page !== null ||
                        cursorOpts?.following?.before ||
                        cursorOpts?.following?.after) &&
                        !isSelectableFinal && (
                            <ResourceListPagination
                                hasCursorPagination={
                                    cursorOpts?.following?.after ||
                                    cursorOpts?.following?.before
                                }
                                page={page}
                                count={hideCount ? null : totalResults}
                                pages={totalPages}
                                hasPrevCursor={cursorOpts?.following?.before}
                                hasNextCursor={cursorOpts?.following?.after}
                                onNav={onPageNav}
                                hasEmptyText={Boolean(emptyText)}
                            ></ResourceListPagination>
                        )}

                    {isSelectableFinal && (
                        <ResourceListSelectedItems
                            loading={
                                propItemsLoading || selectedItemsDataLoading
                            }
                            error={selectedItemsDataError}
                            items={selectedItemsFinal}
                            removeItem={onDeselectItem}
                            getItemText={getSelectedItemText}
                            totalResults={totalResults}
                            setSelectedAll={
                                hasAllBulkSelect && !onSelectedItemsChange
                                    ? setSelectedAll
                                    : null
                            }
                            selectedAll={selectedAll}
                        />
                    )}

                    {itemViews?.length > 1 && (
                        <ResourceListItemViewsToggle
                            itemsView={itemsView}
                            setItemsView={setItemsView}
                            itemViews={itemViews}
                        />
                    )}
                </header>

                {itemsError && (
                    <ErrorMessage section>{itemsError}</ErrorMessage>
                )}

                {getIncludedHtml && !currQueryStr && (
                    <SectionContainer>{getIncludedHtml()}</SectionContainer>
                )}

                <ListLayout
                    itemsView={itemsView}
                    hasWidths={isTable && tableColumns?.some((c) => c.width)}
                    loading={loading}
                >
                    {isTable && !loading && (
                        <>
                            <ResourceListTableColumns
                                tableColumns={tableColumns}
                            />
                        </>
                    )}

                    <ResourceListItems
                        items={allItems}
                        itemView={itemsView}
                        fields={fields}
                        itemClickPath={itemClickPath}
                        itemsNavSearch={itemsNav ? url : ''}
                        onItemClick={onItemClick}
                        onItemSelect={isSelectableFinal ? onItemSelect : null}
                        selectedIds={selectedIds}
                        selectedIdField={selectedIdField}
                        actions={actions}
                        filterQuery={currFilter}
                        selectedAll={selectedAll}
                        getGroup={getGroup}
                    />

                    {!loading && !itemsValidating && !allItems.length
                        ? typeof emptyText !== 'undefined'
                            ? emptyText
                            : 'No data'
                        : null}

                    {isTable && !loading && (
                        <div className="list-table-footer">
                            {tableColumns.map((column) => (
                                <span
                                    style={{
                                        flexBasis: column.width
                                            ? `${column.width}%`
                                            : undefined,
                                    }}
                                    key={column.title}
                                >
                                    {column.getSum
                                        ? column.getSum(allItems)
                                        : column.sumTitle || null}
                                </span>
                            ))}
                        </div>
                    )}
                </ListLayout>

                {paginated &&
                    !cursorPagination &&
                    itemsHasNext &&
                    items?.length &&
                    !propItems?.length && (
                        <div className="load-more-button">
                            <Button
                                fullWidth
                                disabled={loading}
                                onClick={handleLoadMore}
                                text="Load more"
                                small
                                outline
                                isLoading={itemsValidating}
                            />
                        </div>
                    )}
            </div>

            {newItemUrl && renderMainButton && (
                <MainButton
                    label={newItemLabel}
                    functionality="CREATE"
                    onClick={() => {
                        if (loading) return
                        navigate(parseResourcePath(newItemUrl, allParams))
                    }}
                />
            )}
        </>
    )
}

export default ResourceList
